<template>
  <v-navigation-drawer>
    <div class="px-[16px]">
      <header class="flex flex-col mb-[16px]">
        <div class="flex items-center h-20">
          <Button hierarchy="tertiary" size="sm" @click="$emit('close')">
            <IconLayoutSidebarLeftCollapse :size="24" />
          </Button>
        </div>
      </header>
      <div class="flex flex-col gap-2 justify-center mb-[16px]">
        <Button
          v-for="menu in menuItems"
          :key="menu.title"
          hierarchy="custom"
          class="justify-start text-[14px] rounded-md text-ellipsis overflow-hidden whitespace-nowrap"
          :class="
            isActiveRoute(menu.to)
              ? 'bg-[#2663A3] text-[#FFFFFF] font-semibold'
              : 'text-[#525252] hover:bg-gray-200'
          "
          :prefixIcon="menu.icon"
          @click="
            navigateTo({
              path: `${menu.to}`,
              query: { ...useRoute().query },
            })
          "
        >
          <span>{{ menu.title }}</span>
        </Button>
      </div>
    </div>
    <template v-slot:append>
      <div
        class="flex flex-col items-center border-t py-[16px] mx-[16px] gap-[4px]"
      >
        <span class="text-xs text-[#6A7282]">Portal Pembelajaran Wajib</span>
        <span class="text-xs text-[#6A7282]">
          Lembaga Administrasi Negara - {{ new Date().getFullYear() }}
        </span>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script setup>
import { Button } from "@idds/vue";
import {
  IconLayoutDashboard,
  IconSchool,
  IconFileText,
  IconHelp,
  IconNotebook,
  IconLayoutSidebarLeftCollapse,
  IconBook,
  IconBook2,
  IconStack,
  IconHome,
} from "@tabler/icons-vue";

const props = defineProps({
  drawer: { type: Boolean, default: false },
  namaType: { type: String, default: "" },
  isFromRevampPath: { type: Boolean, default: false },
});

defineEmits(["close"]);

const route = useRoute();
const basePathSwajar = computed(
  () =>
    `${props.isFromRevampPath ? "/revamp" : ""}/portal-pembelajaran/swajar-${props.namaType}`,
);
const basePathOrientasi = computed(
  () =>
    `${props.isFromRevampPath ? "/revamp" : ""}/portal-pembelajaran/orientasi-PPPK`,
);
const basePathTerjadwal = computed(
  () =>
    `${props.isFromRevampPath ? "/revamp" : ""}/portal-pembelajaran/terjadwal`,
);
const isSwajar = computed(() => /swajar/.test(route.path));
const isOrientasi = computed(() => /orientasi/.test(route.path));
const isTerjadwal = computed(() => /terjadwal/.test(route.path));
const isLatsarCPNS = computed(() => {
  return useBelajarWajibTerjadwalStore().isLatsarCPNS;
});
const isPPPK = computed(() => props.namaType?.toUpperCase() === "PPPK");

const menuItems = computed(() => {
  if (isSwajar.value) {
    return [
      {
        title: `${props.namaType === "PPPK" ? "Agenda I: Tugas dan Fungsi ASN" : "Materi"}`,
        to: basePathSwajar.value,
        icon: props.namaType === "CPNS" ? IconFileText : IconBook,
      },
      {
        title: props.namaType === "CPNS" ? "Microlearning" : "Jurnal Agenda I",
        to:
          props.namaType === "CPNS"
            ? `${basePathSwajar.value}/microlearning`
            : `${basePathSwajar.value}/jurnal`,
        icon: props.namaType === "CPNS" ? IconLayoutDashboard : IconBook2,
      },
      {
        title: `Evaluasi ${props.namaType === "PPPK" ? " Agenda I" : " Akademik"}`,
        to: `${basePathSwajar.value}/evaluasi-akademik`,
        icon: IconStack,
      },
      ...(isPPPK.value
        ? [
            {
              title: "Agenda II: Nilai dan Etika",
              to: `${basePathSwajar.value}/kurikulum-2`,
              icon: IconBook,
            },
            {
              title: "Jurnal Agenda II",
              to: `${basePathSwajar.value}/kurikulum-2/jurnal`,
              icon: IconBook2,
            },
            {
              title: "Evaluasi Agenda II",
              to: `${basePathSwajar.value}/kurikulum-2/evaluasi-akademik`,
              icon: IconStack,
            },
          ]
        : []),
      { title: "Pusat Bantuan", to: "/pusat-bantuan", icon: IconHelp },
    ];
  } else if (isTerjadwal.value) {
    return [
      {
        title: "Dashboard",
        to: `${basePathTerjadwal.value}`,
        icon: IconLayoutDashboard,
      },
      {
        title: "Kuis",
        to: `${basePathTerjadwal.value}/kuis`,
        icon: IconHelp,
      },
      {
        title: "Self Learning",
        to: `${basePathTerjadwal.value}/self-learning`,
        icon: IconNotebook,
      },
      {
        title: isLatsarCPNS?.value ? "Aktualisasi" : "Proyek Perubahan",
        to: `${basePathTerjadwal.value}/proyek-perubahan`,
        icon: IconFileText,
      },
      {
        title: "Evaluasi Akademik",
        to: `${basePathTerjadwal.value}/evaluasi-akademik`,
        icon: IconSchool,
      },
      {
        title: "Tugas",
        to: `${basePathTerjadwal.value}/tugas`,
        icon: IconBook,
      },
      {
        title: "Pusat Bantuan",
        to: "/pusat-bantuan",
        icon: IconHelp,
      },
    ];
  } else if (isOrientasi.value) {
    return [
      {
        title: "Beranda",
        to: basePathOrientasi.value,
        icon: IconHome,
      },
      {
        title: "Agenda I: Tugas dan Fungsi ASN",
        to: `${basePathOrientasi.value}/agenda-1`,
        icon: IconBook,
      },
      {
        title: "Rangkuman I",
        to: `${basePathOrientasi.value}/agenda-1/rangkuman`,
        icon: IconBook2,
      },
      {
        title: "Evaluasi I",
        to: `${basePathOrientasi.value}/agenda-1/evaluasi-akademik`,
        icon: IconStack,
      },
      {
        title: "Agenda II: Nilai dan Etika",
        to: `${basePathOrientasi.value}/agenda-2`,
        icon: IconBook,
      },
      {
        title: "Rangkuman II",
        to: `${basePathOrientasi.value}/agenda-2/rangkuman`,
        icon: IconBook2,
      },
      {
        title: "Evaluasi II",
        to: `${basePathOrientasi.value}/agenda-2/evaluasi-akademik`,
        icon: IconStack,
      },
      {
        title: "Pusat Bantuan",
        to: "/pusat-bantuan",
        icon: IconHelp,
      },
    ];
  }
});

const isActiveRoute = (to) => {
  return route.path === `${to}`;
};
</script>
